/**
 * @file Keeps the sidebar's Inbox badge fresh (host-inbox-tech-design T10,
 * §4.5.11).
 *
 * The badge has no poller of its own. The bell already polls the unread
 * notification count every 30 s (`use-notification-polling.jsx`), and every
 * new enquiry writes a `host_enquiry` notification and every booking change a
 * `host_booking` one, so a rise in that count is the signal that the inbox may
 * have changed too. This hook refetches
 * `GET host/inbox/summary` (`fetchInboxSummary`):
 *
 *  - when the host area mounts;
 *  - when the window regains focus;
 *  - whenever the bell's polled `unreadCount` rises.
 *
 * Inside the inbox, every response's `counts` update the badge directly
 * (`setInboxCounts`), which is how it drops after a `mark_read` or a confirm
 * and never before.
 *
 * Only an approved travel host has an inbox (T11); for everyone else this does
 * nothing. A failed fetch keeps the last count: the thunk settles as a
 * rejected action rather than throwing, and the badge is a hint, never an
 * error, so nothing here surfaces one.
 *
 * Plain .js: no JSX.
 */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchInboxSummary } from 'store/features/hostInboxSlice';
import { HOST_USER_TYPE } from 'utils/host-area-access';

const selectHasInbox = (state) =>
  Boolean(state.user?.isLoggedIn && state.user?.data?.userType === HOST_USER_TYPE && state.user?.data?.isApproved);

export const useHostInboxBadge = () => {
  const dispatch = useDispatch();
  const hasInbox = useSelector(selectHasInbox);
  const unreadCount = useSelector((state) => state.notification?.unreadCount ?? 0);
  const prevUnreadRef = useRef(unreadCount);

  // Mount, and every return to the tab.
  useEffect(() => {
    if (!hasInbox) return undefined;

    const refresh = () => dispatch(fetchInboxSummary());
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [dispatch, hasInbox]);

  // The bell's count rising means a notification arrived, and a new enquiry
  // always writes one. On a fresh load the first poll reads as a rise from the
  // slice's initial 0, which costs one extra summary call: cheaper than
  // tracking which poll came first.
  useEffect(() => {
    const previous = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (hasInbox && unreadCount > previous) dispatch(fetchInboxSummary());
  }, [dispatch, hasInbox, unreadCount]);
};

export default useHostInboxBadge;
